import type { DocumentVisual } from "../studio/visual-design.js";
import type { ClientId, EquipmentId, InspectionId, ProfessionalId } from "../../shared/kernel/identifiers.js";
import type { LpConsumableType } from "./lp-consumable.js";

/**
 * Relatório de Inspeção por Líquido Penetrante (LP).
 *
 * Mesmo desenho documental do Relatório ME: cada `*Id` registra QUAL cadastro
 * foi escolhido e o texto ao lado registra O QUE ele dizia naquele momento.
 * O documento imprime o texto — uma edição posterior do cadastro nunca
 * reescreve um relatório já emitido.
 */

/** Estados oficiais do laudo LP — nada é aprovado automaticamente. */
export type LpLaudo = "APROVADO" | "REPROVADO" | "EXAME_COMPLEMENTAR" | "EM_ELABORACAO" | "";

export const LP_LAUDO_OPTIONS: ReadonlyArray<{ value: LpLaudo; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "APROVADO", label: "Aprovado" },
  { value: "REPROVADO", label: "Reprovado" },
  { value: "EXAME_COMPLEMENTAR", label: "Exame Complementar" },
  { value: "EM_ELABORACAO", label: "Em Elaboração" }
];

export const LP_LAUDO_LABELS: Record<Exclude<LpLaudo, "">, string> = {
  APROVADO: "Aprovado",
  REPROVADO: "Reprovado",
  EXAME_COMPLEMENTAR: "Exame Complementar",
  EM_ELABORACAO: "Em Elaboração"
};

export function formatLpLaudo(laudo: LpLaudo | null | undefined): string {
  if (!laudo) return "";
  return LP_LAUDO_LABELS[laudo] ?? String(laudo);
}

export type LpPenetrantFamily = "COLORIDO" | "FLUORESCENTE" | "";

export const LP_PENETRANT_FAMILY_OPTIONS: ReadonlyArray<{ value: LpPenetrantFamily; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "COLORIDO", label: "Colorido" },
  { value: "FLUORESCENTE", label: "Fluorescente" }
];

export function formatPenetrantFamily(family: LpPenetrantFamily | null | undefined): string {
  if (family === "COLORIDO") return "Colorido";
  if (family === "FLUORESCENTE") return "Fluorescente";
  return "";
}

/**
 * Tipo de iluminação: LUZ AMBIENTE, LUZ VISÍVEL ou LUZ NEGRA — select, nunca
 * texto livre. Valores gravados por versões anteriores ("LUZ_VISIVEL",
 * "ILUMINACAO_VISIVEL", "Luz Visível (valor anterior)", "LUZ_NEGRA_UV_A"...)
 * continuam reconhecidos e são normalizados para o equivalente ao serem
 * lidos — o relatório histórico não quebra nem perde a informação.
 */
export type LpIlluminationType = "LUZ_AMBIENTE" | "LUZ_VISIVEL" | "LUZ_NEGRA" | "";

export const LP_ILLUMINATION_OPTIONS: ReadonlyArray<{ value: LpIlluminationType; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "LUZ_AMBIENTE", label: "LUZ AMBIENTE" },
  { value: "LUZ_VISIVEL", label: "LUZ VISÍVEL" },
  { value: "LUZ_NEGRA", label: "LUZ NEGRA" }
];

/** Chave de comparação: caixa alta, sem acento, sem "(valor anterior)", só letras. */
function illuminationKey(raw: string): string {
  return raw
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/\(.*?\)/g, "")
    .replace(/[^A-Z]/g, "");
}

/** Equivalências reconhecidas → valor canônico. */
const ILLUMINATION_EQUIVALENTS: Record<string, LpIlluminationType> = {
  LUZAMBIENTE: "LUZ_AMBIENTE",
  AMBIENTE: "LUZ_AMBIENTE",
  LUZVISIVEL: "LUZ_VISIVEL",
  ILUMINACAOVISIVEL: "LUZ_VISIVEL",
  VISIVEL: "LUZ_VISIVEL",
  LUZNEGRA: "LUZ_NEGRA",
  LUZNEGRAUVA: "LUZ_NEGRA",
  NEGRA: "LUZ_NEGRA",
  UVA: "LUZ_NEGRA"
};

/**
 * Normaliza qualquer forma equivalente para o valor canônico. O que não for
 * reconhecido volta como veio — nunca é descartado em silêncio.
 */
export function normalizeIlluminationType(raw: string | null | undefined): LpIlluminationType {
  const value = (raw ?? "").trim();
  if (!value) return "";
  if (LP_ILLUMINATION_OPTIONS.some(o => o.value === value)) return value as LpIlluminationType;
  return ILLUMINATION_EQUIVALENTS[illuminationKey(value)] ?? (value as LpIlluminationType);
}

export function formatIllumination(type: LpIlluminationType | null | undefined): string {
  if (!type) return "";
  const canonical = normalizeIlluminationType(type);
  return LP_ILLUMINATION_OPTIONS.find(o => o.value === canonical)?.label ?? String(type);
}

/** Opções do select: as três oficiais; um valor não reconhecido do relatório é mantido para não sumir. */
export function illuminationOptionsFor(current: LpIlluminationType | null | undefined): ReadonlyArray<{ value: LpIlluminationType; label: string }> {
  const canonical = normalizeIlluminationType(current);
  if (canonical && !LP_ILLUMINATION_OPTIONS.some(o => o.value === canonical)) {
    return [...LP_ILLUMINATION_OPTIONS, { value: canonical, label: String(canonical) }];
  }
  return LP_ILLUMINATION_OPTIONS;
}

/** Linha da tabela "RESULTADOS DA INSPEÇÃO DAS JUNTAS SOLDADAS / ÁREAS". */
export interface LpResultItem {
  /** Identidade interna e imutável da linha — o "ITEM" visível pode ser renumerado. */
  key: string;
  /** "01", "02"... como o usuário quer ver no documento. */
  item: string;
  element: string;
  location: string;
  /** Extensão em mm, guardada como texto já formatado pelo usuário ("50"). */
  extension: string;
  description: string;
  laudo: LpLaudo;
}

/** Consumível como o relatório o guarda: o vínculo mais o texto congelado. */
export interface LpConsumableRef {
  consumableId: string | null;
  brand: string;
  product: string;
  lot: string;
  /** "aaaa-mm-dd" */
  validity: string;
}

export function emptyConsumableRef(): LpConsumableRef {
  return { consumableId: null, brand: "", product: "", lot: "", validity: "" };
}

/** Croqui anexado pelo usuário — a imagem real, nunca uma ilustração. */
export interface LpCroqui {
  /** Data URL (image/*). */
  image: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
}

/** Evidência fotográfica — quantidade livre. */
export interface LpPhoto {
  key: string;
  /** Data URL (image/*). */
  image: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  /** "S1", "Junta 3"... */
  item: string;
  location: string;
  comment: string;
}

export type LpSignatoryRole = "INSPETOR" | "PLH" | "FISCAL";

export interface LpSignatory {
  role: LpSignatoryRole;
  professionalId: ProfessionalId | null;
  name: string;
  functionTitle: string;
  credential: string;
  signatureImageId: string | null;
  /** Data em que este signatário assinou, quando aplicável. */
  signedAt: string | null;
}

export const LP_SIGNATORY_ROLE_LABELS: Record<LpSignatoryRole, string> = {
  INSPETOR: "Inspetor de Equipamentos",
  PLH: "Engenheiro / PLH",
  FISCAL: "Fiscal do Contrato"
};

/** Trilha do próprio registro — o AuditRecorder real descarta eventos, então o histórico vive aqui. */
export interface LpHistoryEntry {
  at: string;
  by: string;
  action: "CREATED" | "SAVED" | "VALIDATED" | "SIGNED" | "PDF_GENERATED";
  detail: string;
}

export type LpReportStatus = "DRAFT" | "VALIDATED" | "SIGNED";

export interface LpReport {
  id: string;
  clientId: ClientId;

  /** "Rel. INSPECT IA nº" — gerado uma vez, nunca regenerado. */
  reportNumber: string;
  /** "Rel. cliente nº" — referência interna do cliente, texto livre. */
  clientReportNumber: string;
  revision: string;
  status: LpReportStatus;

  // Contexto (Cliente → Projeto → Unidade/Área → Equipamento → Inspeção)
  projectId: string | null;
  unitAreaId: string | null;
  equipmentId: EquipmentId | null;
  /** Opcional: um LP pode existir sem Inspeção vinculada. Nunca se inventa uma. */
  inspectionId: InspectionId | null;

  // 01 — DADOS GERAIS E CONTRATUAIS
  clientDisplayName: string;
  projectContract: string;
  /** "Obra / Local" e "Planta / Local" do documento. */
  siteLocation: string;
  unitArea: string;
  equipmentTag: string;
  equipmentTypeLabel: string;
  /** Componente inspecionado ("Tanque de Processo", "Bocal N1"...). */
  component: string;
  /** Nº da inspeção vinculada, como o documento o imprime. */
  inspectionNumber: string;
  osWorkOrderId: string | null;
  osNumber: string;
  osSubWorkOrderId: string | null;
  subOsNumber: string;
  testDate: string;
  inspectorProfessionalId: ProfessionalId | null;
  inspectorName: string;

  procedureId: string | null;
  procedureRevision: string;
  referenceNormId: string | null;
  referenceNorm: string;
  acceptanceCriteriaId: string | null;
  acceptanceCriteria: string;
  /** °C, texto já no formato do usuário ("25,0"). */
  testTemperature: string;

  // 02 — MATERIAIS E CONSUMÍVEIS
  baseMaterial: string;
  thicknessOrDiameter: string;
  penetrantFamily: LpPenetrantFamily;
  illuminationType: LpIlluminationType;
  /** Lux, texto do usuário ("1.000"). */
  illuminationIntensity: string;
  penetrant: LpConsumableRef;
  remover: LpConsumableRef;
  developer: LpConsumableRef;
  /** Minutos, texto do usuário ("10"). */
  penetrationTime: string;

  // 03 — RESULTADOS
  results: LpResultItem[];

  // 04 — CROQUI
  croqui: LpCroqui | null;

  // 05 — EVIDÊNCIAS FOTOGRÁFICAS
  photos: LpPhoto[];

  // 06 — OBSERVAÇÕES E PARECER FINAL (só o que o usuário escreveu)
  observations: string;

  // 07 — SIGNATÁRIOS
  signatories: LpSignatory[];

  /** Data de emissão — congelada no último salvamento. */
  issueDate: string;

  history: LpHistoryEntry[];

  /**
   * Snapshot documental congelado na assinatura: a partir daí Preview e PDF
   * consomem este objeto, não os cadastros vivos. Nulo enquanto rascunho.
   */
  frozenSnapshot: LpDocumentModel | null;

  signedAt: string | null;
  signedByUserId: string | null;
  signedByProfessionalId: ProfessionalId | null;

  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

/**
 * O MESMO modelo alimenta o Preview e o PDF — não existem dois mapeamentos.
 * Tudo aqui já está no formato de apresentação (datas dd/mm/aaaa, rótulos).
 */
export interface LpDocumentModel {
  /** Visual do STUDIO congelado com o modelo. */
  visual?: DocumentVisual | null;
  reportNumber: string;
  clientReportNumber: string;
  revision: string;
  status: LpReportStatus;
  issueDate: string;
  osNumber: string;
  subOsNumber: string;
  /** Payload do QR — identifica o documento; nunca uma imagem decorativa. */
  qrPayload: string;

  identification: {
    client: string;
    projectContract: string;
    unitArea: string;
    siteLocation: string;
    equipmentTag: string;
    equipmentDescription: string;
    component: string;
    /** Vazio quando o LP não tem Inspeção — o documento mostra "—", nunca um id. */
    inspectionNumber: string;
    testDate: string;
    osNumber: string;
    subOsNumber: string;
  };

  testData: {
    procedure: string;
    referenceNorm: string;
    acceptanceCriteria: string;
    baseMaterial: string;
    thicknessOrDiameter: string;
    penetrantFamily: string;
    illuminationType: string;
    illuminationIntensity: string;
    testTemperature: string;
    penetrationTime: string;
    inspector: string;
  };

  consumables: Array<{ type: LpConsumableType; typeLabel: string; brand: string; product: string; lot: string; validity: string; expired: boolean }>;

  results: Array<{ item: string; element: string; location: string; extension: string; description: string; laudo: LpLaudo; laudoLabel: string }>;

  croqui: { image: string; fileName: string } | null;

  photos: Array<{ number: string; image: string; item: string; location: string; comment: string }>;

  observations: string;

  responsibilityTerm: string;

  signatories: Array<{
    role: LpSignatoryRole;
    roleLabel: string;
    name: string;
    functionTitle: string;
    credential: string;
    signatureImage: string | null;
    date: string;
  }>;
}

export const LP_RESPONSIBILITY_TERM =
  "Declaro que o ensaio foi realizado conforme os procedimentos, normas e critérios aplicáveis, com equipamentos e " +
  "consumíveis adequados, e que as informações registradas neste relatório são verdadeiras e representam fielmente os " +
  "resultados obtidos.";

export interface LpValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * "Validar Dados": conferência de completude, sem transição de status.
 * Cada mensagem nomeia o campo faltante — nunca um "dados inválidos".
 */
export function validateLpReportCompleteness(report: LpReport): LpValidationResult {
  const errors: string[] = [];

  if (!report.equipmentTag.trim()) errors.push("Informe o Equipamento / TAG.");
  if (!report.testDate.trim()) errors.push("Informe a Data do Ensaio.");
  if (!report.procedureId && !report.procedureRevision.trim()) errors.push("Selecione o Procedimento LP.");
  if (!report.referenceNormId && !report.referenceNorm.trim()) errors.push("Selecione a Norma de Referência.");
  if (!report.acceptanceCriteriaId && !report.acceptanceCriteria.trim()) errors.push("Selecione o Critério de Aceitação.");
  if (!report.penetrantFamily) errors.push("Selecione a Família de Penetrante.");
  if (!report.illuminationType) errors.push("Selecione o Tipo de Iluminação.");
  if (!report.penetrant.consumableId && !report.penetrant.product.trim()) errors.push("Selecione o Penetrante.");
  if (!report.remover.consumableId && !report.remover.product.trim()) errors.push("Selecione o Removedor / Limpador.");
  if (!report.developer.consumableId && !report.developer.product.trim()) errors.push("Selecione o Revelador.");
  if (!report.inspectorProfessionalId && !report.inspectorName.trim()) errors.push("Informe o Inspetor responsável pelo ensaio.");

  if (report.results.length === 0) {
    errors.push("Adicione ao menos um item na seção Resultados.");
  } else {
    const withoutElement = report.results.filter(r => !r.element.trim()).length;
    if (withoutElement > 0) errors.push(`Existem ${withoutElement} ${withoutElement === 1 ? "item" : "itens"} sem Junta / Elemento informado.`);
    const withoutLaudo = report.results.filter(r => !r.laudo).length;
    if (withoutLaudo > 0) errors.push(`Existem ${withoutLaudo} ${withoutLaudo === 1 ? "item" : "itens"} sem Laudo Técnico.`);
  }

  const withoutPhotoItem = report.photos.filter(p => !p.item.trim()).length;
  if (withoutPhotoItem > 0) errors.push(`Existem ${withoutPhotoItem} ${withoutPhotoItem === 1 ? "fotografia" : "fotografias"} sem Item / Componente informado.`);

  return { valid: errors.length === 0, errors };
}

/**
 * Traz para a forma atual um relatório gravado por versão anterior do
 * modelo: coleções ausentes viram vazias, referências de consumível ausentes
 * viram vazias. Abrir um relatório antigo não pode mostrar campos quebrados.
 */
export function normalizeLpReport(report: LpReport): LpReport {
  const r = report as any;
  if (!Array.isArray(r.results)) r.results = [];
  if (!Array.isArray(r.photos)) r.photos = [];
  if (!Array.isArray(r.signatories)) r.signatories = [];
  if (!Array.isArray(r.history)) r.history = [];
  if (!r.penetrant) r.penetrant = emptyConsumableRef();
  if (!r.remover) r.remover = emptyConsumableRef();
  if (!r.developer) r.developer = emptyConsumableRef();
  if (r.croqui === undefined) r.croqui = null;
  if (r.frozenSnapshot === undefined) r.frozenSnapshot = null;
  if (r.inspectionId === undefined) r.inspectionId = null;
  // Iluminação: formas antigas equivalentes viram o valor canônico ao ler.
  r.illuminationType = normalizeIlluminationType(r.illuminationType);
  if (r.osSubWorkOrderId === undefined) r.osSubWorkOrderId = null;
  if (typeof r.subOsNumber !== "string") r.subOsNumber = "";

  r.results = r.results.map((raw: any, index: number) => ({
    key: raw.key || `LP-ITEM-LEGACY-${index}`,
    item: raw.item ?? String(index + 1).padStart(2, "0"),
    element: raw.element ?? "",
    location: raw.location ?? "",
    extension: raw.extension ?? "",
    description: raw.description ?? "",
    laudo: raw.laudo ?? ""
  }));

  r.photos = r.photos.map((raw: any, index: number) => ({
    key: raw.key || `LP-PHOTO-LEGACY-${index}`,
    image: raw.image ?? "",
    fileName: raw.fileName ?? "",
    fileSize: Number(raw.fileSize ?? 0),
    uploadedAt: raw.uploadedAt ?? "",
    item: raw.item ?? "",
    location: raw.location ?? "",
    comment: raw.comment ?? ""
  }));

  return report;
}

/** "01", "02"... o próximo número livre para uma nova linha de resultado. */
export function suggestNextLpItemNumber(existing: readonly string[]): string {
  let max = 0;
  for (const value of existing) {
    const n = parseInt(String(value).replace(/[^0-9]/g, ""), 10);
    if (!isNaN(n) && n > max) max = n;
  }
  return String(max + 1).padStart(2, "0");
}

/** Só imagens entram no documento — um PDF anexado por engano não quebra a geração. */
export function isEmbeddableImage(dataUrl: string | null | undefined): boolean {
  return typeof dataUrl === "string" && /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(dataUrl);
}

/**
 * Distribui as fotografias em linhas de `perRow` para o documento. Cada linha
 * é indivisível na paginação; linhas seguem para as próximas páginas na
 * quantidade que forem — sem comprimir tudo numa página e sem página vazia.
 */
export function chunkPhotos<T>(photos: readonly T[], perRow: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < photos.length; i += perRow) rows.push(photos.slice(i, i + perRow));
  return rows;
}
